import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchPeople, IUsershipPerson } from '@query'
import { useBusinessContext } from '@context'
import { Button, ModalV2, Switch } from '@components'
import styled from 'styled-components'
import { createFullName } from '@utils'
import { useTranslation } from 'react-i18next'
import { FaCheck } from 'react-icons/fa'

type OnChange = (value: number[]) => void

interface Props {
  header?: string
  value: number[]
  onChange?: OnChange
  renderItem: (item: IUsershipPerson) => React.ReactNode
  renderAddButton?: (edit: () => void) => React.ReactNode
  disabled?: boolean
}

export const UserPicker: React.FC<Props> = ({
  header,
  renderItem,
  value,
  onChange,
  renderAddButton,
  disabled = false
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const { slug } = useBusinessContext()
  const { t } = useTranslation()
  const { data } = useQuery({
    queryKey: [slug, 'people'],
    queryFn: () => fetchPeople({ slug }, { page_size: 1000 })
  })
  const allUserships = data?.results || []
  const userships = allUserships.filter(({ user_id: userId }) => value.includes(userId))

  return (
    <Wrapper>
      <SelectedUsers>{userships.map(item => renderItem(item))}</SelectedUsers>
      {!disabled && renderAddButton && (
        <ButtonFooter>{renderAddButton(() => setIsVisible(true))}</ButtonFooter>
      )}

      {!disabled && (
        <ModalV2
          header={header}
          isVisible={isVisible}
          handleOnClose={() => setIsVisible(false)}
          footer={
            <Footer>
              <Button
                intent="success"
                icon={<FaCheck />}
                type="button"
                onClick={() => setIsVisible(false)}
              >
                {t('general.done')}
              </Button>
            </Footer>
          }
        >
          <UserItems>
            {allUserships.map(item => (
              <UserItem key={item.user_id}>
                <Switch
                  id={`picker-${item.user_id}`}
                  checked={value.includes(item.user_id)}
                  onChange={e => {
                    e.target.checked
                      ? onChange([...value, item.user_id])
                      : onChange(value.filter(id => id !== item.user_id))
                  }}
                />
                <UserInfo htmlFor={`picker-${item.user_id}`}>
                  <b>{createFullName(item)}</b>
                  <span>{item.email}</span>
                </UserInfo>
              </UserItem>
            ))}
          </UserItems>
        </ModalV2>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  & > * {
    margin: 0;
    padding: 0;
  }
`

const UserItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl}rem;
`

const UserInfo = styled.label`
  display: flex;
  flex-direction: column;
`

const SelectedUsers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const ButtonFooter = styled.div`
  display: flex;
`

const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`
